<template>
  <v-row no-gutters>
    <v-col cols="12">
      <draggable-lists
        v-bind="$attrs"
        disable-multi-drag
        :leftTitle="leftTitle"
        :rightTitle="rightTitle"
        :left-list="computedLayersGroups"
        :right-list="searchedLayers"
        @removedFromGroups="onLayersRemovedFromGroup"
        @elementAdded="onLayersAddedToGroup"
        @addGroup="openElementDialog($event, true, false)"
        @edit="openElementDialog($event, true, true)"
        @delete="openDeleteDialog"
        :disabled="disabled"
      >
        <template #listElement="element">
          <draggable-lists-mobile-layers-item
            :key="element.id"
            v-bind="element"
            v-on="element.listeners"
            :disabled="disabled"
            :dotsItems="[{ name: 'edit' }, { name: 'delete' }]"
            :name-key="element.level === 1 ? lng : ''"
            @toggleHideGroup="onGroupVisibleToggle(element)"
          >
            <template #prependName="{ level }">
              <v-col v-if="level === 1 && element.icon?.length" class="mr-1" style="width: 25px; height: 25px">
                <img :src="$_base64ToUrl(element.icon, { autoExtention: true })" width="25" height="25" />
              </v-col>
            </template>
          </draggable-lists-mobile-layers-item>
        </template>
        <template #rightListElement="element">
          <draggable-lists-mobile-layers-item
            :key="element.id"
            v-bind="element"
            :disabled="disabled"
            :level="1"
            is-right
          />
        </template>
        <template #search>
          <data-input
            :data-type="{ name: 'text' }"
            prepend-inner-icon="mdi-magnify"
            :placeholder="$i18n.t('default.find')"
            v-model="search"
            clearable
            height="32"
            hide-details
            :disabled="disabled"
            class="search-input"
          >
          </data-input>
        </template>
      </draggable-lists>
      <main-input-dialog
        v-if="isElementDialogLoaded"
        :is-visible.sync="isElementDialogVisible"
        :title-translation="elementDialogTitle"
        :agree-text="elementDialogAgree"
        @agree="elementDialogAction"
        :inputs="elementDialogInputs"
        :loading="dialogLoading"
      >
        <template v-for="(input, idx) of elementDialogInputs" #[`${input.name}.input`]="data">
          <v-col cols="6" :key="idx">
            <data-input v-model="currentElement[input.name]" v-bind="data" />
          </v-col>
        </template>
        <template #icon.input="data">
          <v-col cols="6">
            <file-input ref="file" v-bind="data" />
          </v-col>
        </template>
      </main-input-dialog>
      <main-dialog
        v-if="isDeleteDialogLoaded"
        :is-visible.sync="isDeleteDialogVisible"
        :title="deleteDialogTitle"
        :body="deleteDialogBody"
        @agree="deleteGroup"
        :loading="dialogLoading"
        agree-text="dialog.agreeDelete"
      />
    </v-col>
  </v-row>
</template>
<script>
const lngs = ['pl', 'en', 'de'];
import { call, get } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import DraggableLists from '@/components/DraggableLists';
import DraggableListsMobileLayersItem from '@/components/DraggableListsMobileLayersItem';
import FileInput from '@/components/FileInput';

export default {
  name: 'IlsEasySettingsLayers',
  components: {
    DataInput,
    DraggableLists,
    DraggableListsMobileLayersItem,
    FileInput,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    leftTitle: {
      type: String,
      default: 'admin.ilsEasy.ilsEasyLayers',
    },
    rightTitle: {
      type: String,
      default: 'admin.availableLayers',
    },
  },
  computed: {
    lng() {
      return process.env.VUE_APP_I18N_LOCALE;
    },
    layersGroupNameMaxLength: get('admin/settingsValues@layers_group_name_max_length'),
    layers: get('layers/layers'),
    layersArray() {
      return this.$_objectToArray(this.layers);
    },
    searchedLayers() {
      return this.layersArray.filter(
        layer =>
          layer.layer_scope === 'core' &&
          (!this.search || layer.name.toLowerCase().includes(this.search.toLowerCase())) &&
          !this.computedFlatLayersIds.includes(layer.id)
      );
    },
    ilsLayersGroups: get('ils_easy/layers'),
    computedLayersGroups() {
      return JSON.parse(JSON.stringify(this.ilsLayersGroups));
    },
    computedFlatLayersIds() {
      return this.$_flattenStructure(this.ilsLayersGroups, 'layers').map(layer => layer.layer_id);
    },

    deleteDialogTitle() {
      return this.$i18n.t('dialog.title.deleteGroup');
    },
    deleteDialogBody() {
      return this.$i18n.t('dialog.body.deleteLayersGroup', {
        name: this.currentElement.name?.[this.lng] || this.currentElement.name?.['pl'],
      });
    },
    elementDialogTitle() {
      return this.$i18n.t(this.isCurrentElementEdit ? 'dialog.title.manageGroup' : 'dialog.title.addDraggableGroup');
    },
    elementDialogAgree() {
      return this.isCurrentElementEdit ? 'button.save' : 'dialog.agreeAdd';
    },
    elementDialogAction() {
      return this.isCurrentElementEdit ? this.editGroup : this.addGroup;
    },
    elementDialogDropzoneOptions() {
      return {
        url: '/',
        method: 'post',
        autoProcessQueue: false,
        uploadMultiple: false,
        addRemoveLinks: true,
        paramName: 'file',
        acceptedFiles: '.png,.svg',
        maxFiles: 1,
      };
    },
    elementDialogInputs() {
      return [
        ...lngs.map(lng => {
          return {
            name: `name_${lng}`,
            translationPath: `admin.ilsEasy.layersGroups.name${this.$_capitalize(lng)}`,
            dataType: {
              name: 'text',
              max_length: this.layersGroupNameMaxLength,
            },
            rules: [v => !!v || 'rules.required'],
          };
        }),
        {
          name: 'icon',
          translationPath: 'admin.ilsEasy.layersGroups.icon',
          subtitle: this.$i18n.t('admin.ilsEasy.layersGroups.iconSubtitle'),
          options: this.elementDialogDropzoneOptions,
          showAddedFiles: true,
          events: {
            'vdropzone-file-added': this.onFileAdded,
            'vdropzone-removed-file': this.onFileRemoved,
          },
        },
      ];
    },
    isCurrentElementEdit() {
      return this.currentElement?.id || this.currentElement?.id === 0;
    },
  },
  data: () => ({
    currentElement: {},
    search: '',

    isElementDialogLoaded: false,
    isElementDialogVisible: false,
    isDeleteDialogLoaded: false,
    isDeleteDialogVisible: false,
    dialogLoading: false,
  }),
  methods: {
    addLayersGroup: call('ils_easy/addIlsLayersGroup'),
    editLayerInGroup: call('ils_easy/editIlsLayerInGroup'),
    deleteLayersGroup: call('ils_easy/deleteIlsLayersGroup'),
    editLayersGroup: call('ils_easy/editIlsLayersGroup'),
    onGroupVisibleToggle({ index }) {
      this.$set(this.computedLayersGroups[index], 'isHidden', !this.computedLayersGroups[index]?.isHidden);
      this.$forceUpdate();
    },
    onLayersAddedToGroup(element) {
      const { toGroup, elementId, order_in_group, isGroup } = element;
      if (!isGroup) {
        this.wrapper('editLayerInGroup', {
          id: +elementId[0].layer_id,
          payload: {
            group_id: +toGroup,
            order_in_group: +order_in_group + 1,
          },
        });
      } else {
        this.wrapper('editLayersGroup', {
          id: +elementId[0],
          payload: {
            order: +order_in_group + 1,
          },
        });
      }
    },
    onLayersRemovedFromGroup(e) {
      const element = e[0]?.element;
      this.wrapper('editLayerInGroup', {
        id: element.id,
        payload: {
          group_id: null,
        },
      });
    },
    async onFileAdded(file) {
      const fileAsBase64 = await this.$_fileToBase64(file);
      this.$set(this.currentElement, 'icon', fileAsBase64);
    },
    onFileRemoved() {
      this.$set(this.currentElement, 'icon', '');
    },
    addFile(base64) {
      this.$refs.file?.$refs.dropzone?.addFile(this.$_base64ToFile(base64, { autoExtention: true }));
    },
    removeFile() {
      this.$refs.file?.$refs.dropzone?.removeAllFiles();
    },
    openElementDialog(element = {}) {
      const { name = {} } = element;
      lngs.forEach(lng => {
        element[`name_${lng}`] = name[lng] || '';
      });

      this.currentElement = JSON.parse(JSON.stringify({ ...element, icon: element.icon || '' }));
      this.isElementDialogLoaded = true;
      this.isElementDialogVisible = true;
    },
    openDeleteDialog(element) {
      this.currentElement = JSON.parse(JSON.stringify(element));
      this.isDeleteDialogLoaded = true;
      this.isDeleteDialogVisible = true;
    },

    async wrapper(action, payload) {
      this.dialogLoading = true;
      try {
        await this[action](payload);
        this.isElementDialogVisible = false;
        this.isDeleteDialogVisible = false;
      } finally {
        this.dialogLoading = false;
      }
    },
    async addGroup() {
      const name = lngs.reduce((total, lng) => {
        total[lng] = this.currentElement[`name_${lng}`];
        return total;
      }, {});
      const payload = {
        name,
        icon: this.currentElement.icon,
        order: 1,
      };
      await this.wrapper('addLayersGroup', payload);
    },
    async deleteGroup() {
      await this.wrapper('deleteLayersGroup', this.currentElement.id);
    },
    async editGroup() {
      const name = lngs.reduce((total, lng) => {
        total[lng] = this.currentElement[`name_${lng}`];
        return total;
      }, {});
      const payload = {
        name,
        icon: this.currentElement.icon,
        order: this.currentElement.order + 1,
      };
      await this.wrapper('editLayersGroup', { id: this.currentElement.id, payload });
    },
  },
  mounted() {
    this.$watch(
      vm => {
        return {
          isDeleteDialogVisible: vm.isDeleteDialogVisible,
          isElementDialogVisible: vm.isElementDialogVisible,
          currentElement: vm.currentElement,
        };
      },
      (nV, oV) => {
        const { isDeleteDialogVisible, isElementDialogVisible, currentElement } = nV;
        if (!isDeleteDialogVisible && !isElementDialogVisible) {
          if (JSON.stringify(currentElement) !== JSON.stringify(oV.currentElement)) this.currentElement = {};
          setTimeout(() => {
            this.removeFile();
          });
        } else if (currentElement.icon && currentElement.icon !== oV?.currentElement.icon) {
          setTimeout(() => {
            this.addFile(currentElement.icon);
          });
        } else if (!currentElement.icon && currentElement.icon !== oV?.currentElement.icon) {
          setTimeout(() => {
            this.removeFile();
          });
        }
      },
      { deep: true }
    );
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .search-input {
    .v-input__slot {
      min-height: 0px !important;
      padding: 0 4px !important;
    }
    .v-input__append-inner {
      margin: 6px !important;
    }
    .v-input__prepend-inner {
      .v-input__icon {
        .v-icon {
          font-size: 16px;
          padding-bottom: 5px;
        }
      }
    }
    .v-text-field__slot input {
      font-size: 12px !important;
    }
  }
}
</style>
